import { memo, useMemo, type FC } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { cn } from "@/lib/utils";
import { sanitizationSchema } from "./markdown-sanitization";

/**
 * StreamingMarkdownText component that renders markdown content as it streams in real-time.
 * Optimized for incremental content updates while maintaining proper markdown rendering.
 *
 * @component
 * @example
 * // Basic usage with streaming content
 * <StreamingMarkdownText content={streamingText} />
 *
 * @example
 * // With custom styling
 * <StreamingMarkdownText
 *   content={streamingText}
 *   className="custom-markdown-styles"
 * />
 */
interface StreamingMarkdownTextProps {
  /** The markdown content to render (can be partial/incomplete) */
  content: string;
  /** Optional CSS class name for styling */
  className?: string;
}

/**
 * Preprocesses streaming markdown content to ensure proper rendering of incomplete text.
 * Handles common streaming scenarios like incomplete code blocks, lists, and tables.
 */
const preprocessStreamingContent = (content: string): string => {
  if (!content) return "";

  // Handle incomplete code blocks - close them temporarily for proper rendering
  const codeBlockMatches = content.match(/```/g);
  if (codeBlockMatches && codeBlockMatches.length % 2 !== 0) {
    // We have an unclosed code block, close it temporarily
    content = content + "\n```";
  }

  // Handle incomplete inline code - close backticks
  const inlineCodeMatches = content.match(/(?<!\\)`/g);
  if (inlineCodeMatches && inlineCodeMatches.length % 2 !== 0) {
    // We have an unclosed inline code span, close it temporarily
    content = content + "`";
  }

  // Handle incomplete tables - ensure proper table structure
  const lines = content.split("\n");
  const lastLine = lines[lines.length - 1];

  // If we're in a table row that's incomplete, don't break the table structure
  if (lastLine && lastLine.includes("|") && !lastLine.trim().endsWith("|")) {
    // This looks like an incomplete table row, let markdown handle it naturally
  }

  return content;
};

const StreamingMarkdownTextImpl: FC<StreamingMarkdownTextProps> = ({
  content,
  className
}) => {
  // Preprocess content to handle streaming edge cases
  const processedContent = useMemo(() => {
    return preprocessStreamingContent(content);
  }, [content]);

  // Optimized markdown components for streaming performance
  const markdownComponents = useMemo(() => ({
    h1: ({ className: cnProp, ...props }: any) => (
      <h1 className={cn("mb-8 scroll-m-20 text-4xl font-extrabold tracking-tight last:mb-0", cnProp)} {...props} />
    ),
    h2: ({ className: cnProp, ...props }: any) => (
      <h2 className={cn("mb-4 mt-8 scroll-m-20 text-3xl font-semibold tracking-tight first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    h3: ({ className: cnProp, ...props }: any) => (
      <h3 className={cn("mb-4 mt-6 scroll-m-20 text-2xl font-semibold tracking-tight first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    h4: ({ className: cnProp, ...props }: any) => (
      <h4 className={cn("mb-4 mt-6 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    h5: ({ className: cnProp, ...props }: any) => (
      <h5 className={cn("my-4 text-lg font-semibold first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    h6: ({ className: cnProp, ...props }: any) => (
      <h6 className={cn("my-4 font-semibold first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    p: ({ className: cnProp, ...props }: any) => (
      <p className={cn("mb-5 mt-5 leading-7 first:mt-0 last:mb-0", cnProp)} {...props} />
    ),
    a: ({ className: cnProp, ...props }: any) => (
      <a className={cn("text-primary font-medium underline underline-offset-4", cnProp)} {...props} />
    ),
    blockquote: ({ className: cnProp, ...props }: any) => (
      <blockquote className={cn("border-l-2 pl-6 italic", cnProp)} {...props} />
    ),
    ul: ({ className: cnProp, ...props }: any) => (
      <ul className={cn("my-5 ml-6 list-disc [&>li]:mt-2", cnProp)} {...props} />
    ),
    ol: ({ className: cnProp, ...props }: any) => (
      <ol className={cn("my-5 ml-6 list-decimal [&>li]:mt-2", cnProp)} {...props} />
    ),
    li: ({ className: cnProp, ...props }: any) => (
      <li className={cn("mb-2", cnProp)} {...props} />
    ),
    hr: ({ className: cnProp, ...props }: any) => (
      <hr className={cn("my-5 border-b", cnProp)} {...props} />
    ),
    table: ({ className: cnProp, ...props }: any) => (
      <div className="my-5 overflow-x-auto">
        <table className={cn("w-full border-separate border-spacing-0", cnProp)} {...props} />
      </div>
    ),
    thead: ({ className: cnProp, ...props }: any) => (
      <thead className={cnProp} {...props} />
    ),
    tbody: ({ className: cnProp, ...props }: any) => (
      <tbody className={cnProp} {...props} />
    ),
    tr: ({ className: cnProp, ...props }: any) => (
      <tr className={cn("border-b border-border", cnProp)} {...props} />
    ),
    th: ({ className: cnProp, ...props }: any) => (
      <th className={cn("px-4 py-2 text-left font-semibold border-r border-border last:border-r-0", cnProp)} {...props} />
    ),
    td: ({ className: cnProp, ...props }: any) => (
      <td className={cn("px-4 py-2 border-r border-border last:border-r-0", cnProp)} {...props} />
    ),
    code: ({ className: cnProp, inline, children, ...props }: any) => {
      if (inline) {
        return (
          <code className={cn("bg-muted px-1 py-0.5 rounded text-sm font-mono", cnProp)} {...props}>
            {children}
          </code>
        );
      }

      return (
        <div className="my-4 overflow-x-auto">
          <pre className="bg-muted p-4 rounded-lg">
            <code className={cn("text-sm font-mono", cnProp)} {...props}>
              {children}
            </code>
          </pre>
        </div>
      );
    },
    strong: ({ className: cnProp, ...props }: any) => (
      <strong className={cn("font-semibold", cnProp)} {...props} />
    ),
    em: ({ className: cnProp, ...props }: any) => (
      <em className={cn("italic", cnProp)} {...props} />
    ),
  }), []);

  if (!processedContent) {
    return null;
  }

  return (
    <div className={cn("aui-md max-w-full min-w-0 wrap-break-word overflow-wrap-anywhere", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, sanitizationSchema],
        ]}
        components={markdownComponents}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

/**
 * Memoized StreamingMarkdownText component for optimal performance during streaming updates.
 * Only re-renders when content actually changes.
 */
export const StreamingMarkdownText = memo(StreamingMarkdownTextImpl);
